// src/services/chatAttachments.js
//
// Chat v2 image attachments. Compresses iPhone-sized photos
// down to ~500KB before uploading to Firebase Storage, then
// writes the message doc with attachment metadata. The Cloud
// Function `onChatMessageCreated_v2` (Phase 1) handles the
// lastMessage denormalization and push fan-out.
//
// Path layout (matches storage.rules):
//   chat-attachments/{chatId}/{messageId}/original.jpg
//
// We pre-allocate the messageId via doc(collection(...)).id so
// the upload path is deterministic and tied to the eventual
// message doc. After upload, setDoc the message with that exact
// id and the download URL.

import imageCompression from 'browser-image-compression'
import {
  collection, doc, setDoc, serverTimestamp,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase/firebase'

const COMPRESSION_OPTS = {
  // Cap at ~1600px on the longest side so chat images are
  // sharp on retina but cheap to ship. Most coaches send
  // game-day photos which look great at this size.
  maxWidthOrHeight: 1600,
  // Aim for ~500KB. browser-image-compression iterates until
  // it hits this target (or gives up gracefully).
  maxSizeMB: 0.5,
  // Use JPEG output for predictable cross-device rendering.
  // HEIC from iOS gets implicitly decoded by the canvas in the
  // worker thread, so end recipients always see a JPEG.
  fileType: 'image/jpeg',
  initialQuality: 0.85,
  useWebWorker: true,
}

/**
 * Get the natural width/height of a Blob image. Returns
 * { width, height } or { width: null, height: null } if
 * decoding fails. Used for layout hints in the message bubble.
 */
async function probeDimensions(blob) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(blob)
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve({ width: img.naturalWidth, height: img.naturalHeight })
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        resolve({ width: null, height: null })
      }
      img.src = url
    } catch {
      resolve({ width: null, height: null })
    }
  })
}

/**
 * Send an image message to a chat. Compress -> upload -> write
 * message doc atomically (well, sequentially: failures at any
 * step abort cleanly without leaving orphan storage objects to
 * worry about, since the upload uses the messageId path and
 * never gets referenced if the doc write fails).
 *
 * @param {Object} params
 * @param {string} params.chatId
 * @param {string} params.senderId
 * @param {string} params.senderName
 * @param {string|null} params.senderPhotoURL
 * @param {File} params.file
 * @param {string} [params.caption]
 * @param {function(progress)} [params.onProgress] 0..100 for compression+upload
 * @returns {Promise<string>} the new message id
 */
export async function sendChatImage({
  chatId,
  senderId,
  senderName,
  senderPhotoURL = null,
  file,
  caption = '',
  onProgress = () => {},
}) {
  if (!chatId) throw new Error('chatId required')
  if (!senderId) throw new Error('senderId required')
  if (!file) throw new Error('file required')
  if (!file.type?.startsWith('image/')) {
    throw new Error('Only image files are supported.')
  }
  // Storage rules cap at 10MB. Pre-check so we don't burn time
  // compressing a file that won't be allowed in.
  const ORIGINAL_LIMIT = 30 * 1024 * 1024 // 30MB raw input cap
  if (file.size > ORIGINAL_LIMIT) {
    throw new Error('Image is too large (max 30MB).')
  }

  onProgress(5)

  // 1. Compress (skip if it's already small)
  let blob = file
  if (file.size > 500 * 1024) {
    try {
      blob = await imageCompression(file, COMPRESSION_OPTS)
    } catch (err) {
      console.warn('[chatAttachments] compression failed, uploading original:', err?.message)
      // Fall back to original; storage.rules will still enforce
      // the 10MB cap, so very large iPhone photos will reject
      // here with a clear message.
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Image is too large to upload (max 10MB after compression).')
      }
    }
  }

  onProgress(40)

  // 2. Probe dimensions (non-fatal; nice for bubble sizing)
  const { width, height } = await probeDimensions(blob)

  onProgress(50)

  // 3. Pre-allocate the message id so the storage path matches
  const messageRef = doc(collection(db, 'chats', chatId, 'messages'))
  const messageId = messageRef.id

  // Use the compressed blob's type, fall back to jpeg
  const contentType = blob.type || 'image/jpeg'
  const ext = contentType.includes('png') ? 'png'
            : contentType.includes('webp') ? 'webp'
            : contentType.includes('gif') ? 'gif'
            : 'jpg'
  const storagePath = `chat-attachments/${chatId}/${messageId}/original.${ext}`

  onProgress(60)

  // 4. Upload to Storage
  const fileRef = ref(storage, storagePath)
  await uploadBytes(fileRef, blob, {
    cacheControl: 'public,max-age=31536000,immutable',
    contentType,
  })

  onProgress(85)

  const url = await getDownloadURL(fileRef)

  onProgress(95)

  // 5. Write the message doc with attachment metadata
  await setDoc(messageRef, {
    senderId,
    senderName: senderName || 'Member',
    senderPhotoURL: senderPhotoURL || null,
    type: 'image',
    text: caption?.trim() || null,
    attachment: {
      url,
      storagePath,
      name: file.name || `image.${ext}`,
      size: blob.size,
      contentType,
      width,
      height,
      // No separate thumbnail in MVP; the renderer just uses
      // `url` for the thumb. A Cloud Function could generate
      // a smaller thumbnail later for inbox previews.
      thumbnailUrl: null,
    },
    sentAt: serverTimestamp(),
    edited: false,
    editedAt: null,
    deletedAt: null,
  })

  onProgress(100)
  return messageId
}

// Storage rules cap PDFs at 10MB. The composer pre-checks too,
// but we re-validate here so direct callers (tests, other entry
// points) can't bypass the limit.
const PDF_MAX_BYTES = 10 * 1024 * 1024

/**
 * Send a PDF message to a chat. PDFs are uploaded as-is (no
 * compression — they're already compressed by the producing tool
 * and any further compression would risk corrupting form fields).
 *
 * Path layout (parallel to images, keeps mime-type-based discovery
 * clean):
 *   chat-attachments/{chatId}/{messageId}/document.pdf
 *
 * The message doc's `attachment.type` is 'pdf' so the renderer can
 * branch cleanly between image vs document UI.
 *
 * @param {Object} params
 * @param {string} params.chatId
 * @param {string} params.senderId
 * @param {string} params.senderName
 * @param {string|null} params.senderPhotoURL
 * @param {File} params.file
 * @param {string} [params.caption]
 * @param {function(progress)} [params.onProgress] 0..100 for upload
 * @returns {Promise<string>} the new message id
 */
export async function sendChatPdf({
  chatId,
  senderId,
  senderName,
  senderPhotoURL = null,
  file,
  caption = '',
  onProgress = () => {},
}) {
  if (!chatId) throw new Error('chatId required')
  if (!senderId) throw new Error('senderId required')
  if (!file) throw new Error('file required')
  if (file.type !== 'application/pdf') {
    throw new Error('Only PDF files are supported here.')
  }
  if (file.size > PDF_MAX_BYTES) {
    throw new Error('PDFs must be under 10 MB.')
  }

  onProgress(10)

  // Pre-allocate the message id so the storage path matches the
  // eventual message doc (same pattern as image uploads above).
  const messageRef = doc(collection(db, 'chats', chatId, 'messages'))
  const messageId = messageRef.id

  const storagePath = `chat-attachments/${chatId}/${messageId}/document.pdf`

  onProgress(30)

  // Upload to Storage. Don't slap an immutable cache header on PDFs
  // — coaches occasionally want to reupload an updated form under
  // the same conversation, and a long max-age would mask that.
  const fileRef = ref(storage, storagePath)
  await uploadBytes(fileRef, file, {
    cacheControl: 'public,max-age=3600',
    contentType: 'application/pdf',
    contentDisposition: `inline; filename="${(file.name || 'document.pdf').replace(/"/g, '')}"`,
  })

  onProgress(80)

  const url = await getDownloadURL(fileRef)

  onProgress(95)

  // Write the message doc with attachment metadata. `type: 'pdf'`
  // on both the message and the attachment so existing list/inbox
  // surfaces that key off message.type still work.
  await setDoc(messageRef, {
    senderId,
    senderName: senderName || 'Member',
    senderPhotoURL: senderPhotoURL || null,
    type: 'pdf',
    text: caption?.trim() || null,
    attachment: {
      type: 'pdf',
      url,
      storagePath,
      fileName: file.name || 'document.pdf',
      // Mirror the image attachment's `name`/`size` keys too so any
      // generic "attachment summary" code can read either shape.
      name: file.name || 'document.pdf',
      sizeBytes: file.size,
      size: file.size,
      contentType: 'application/pdf',
    },
    sentAt: serverTimestamp(),
    edited: false,
    editedAt: null,
    deletedAt: null,
  })

  onProgress(100)
  return messageId
}
